import { TradePriceInfo } from '@/types'
import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Info } from 'components/common/Info'
import { Input } from 'components/common/Input'
import { Separator } from 'components/common/Separator'
import { Spinner } from 'components/common/Spinner'
import { Step } from 'components/common/Step'
import { Stepper } from 'components/common/Stepper'
import { TradeButton } from 'components/common/TradeButton'
import { useWaysTo } from 'components/common/WaysTo'
import { useExistingTrade } from 'hooks/useExistingTrade'
import throttle from 'lodash/throttle'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import {
  ComponentProps,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import Skeleton from 'react-loading-skeleton'
import { ContainerElement } from 'types'
import { Artist, Edition } from 'utils/data'
import { getArtworkUrlFromRoot } from 'utils/urls'
import { useOverlay } from '../../contexts'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import { pickTradePrice } from 'utils/currencies'

type IsLoadingType = 'bidOrBuy' | 'bid' | 'askOrSell' | 'ask'

type IsLoadingInterface = {
  [key in IsLoadingType]: boolean
}

type HowItWorksProps = {
  showAgainHandler: (e: React.ChangeEvent<HTMLInputElement>) => void
  href: string
  isSell?: boolean
}

const HowItWorks = ({ showAgainHandler, href, isSell }: HowItWorksProps) => {
  const { t } = useTranslation('common')
  const { setIsOpen } = useOverlay()
  const [isLoading, setIsLoading] = useState(false)

  const buySteps: ComponentProps<typeof Step>[] = [
    {
      id: 1,
      title: t('buy_1_title'),
      description: (
        <div>
          <span>{t('buy_1_description')}</span>
          <ol className='list-outside list-decimal pl-5'>
            <li>
              <Trans
                i18nKey='common:buy_1_description_1'
                components={{ b: <b /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey='common:buy_1_description_2'
                components={{ b: <b /> }}
              />
            </li>
          </ol>
        </div>
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 2,
      title: t('buy_2_title'),
      description: (
        <Trans i18nKey='common:buy_2_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 3,
      title: t('buy_3_title'),
      description: (
        <Trans i18nKey='common:buy_3_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
  ]

  const sellSteps: ComponentProps<typeof Step>[] = [
    {
      id: 1,
      title: t('sell_1_title'),
      description: (
        <div className='flex flex-col'>
          <span>{t('sell_1_description')}</span>
          <ol className='list-outside list-decimal pl-5'>
            <li>
              <Trans
                i18nKey='common:sell_1_description_1'
                components={{ b: <b /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey='common:sell_1_description_2'
                components={{ b: <b /> }}
              />
            </li>
          </ol>
        </div>
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 2,
      title: t('sell_2_title'),
      description: (
        <Trans i18nKey='common:sell_2_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
    {
      id: 3,
      title: t('sell_3_title'),
      description: (
        <Trans i18nKey='common:sell_3_description' components={{ b: <b /> }} />
      ),
      infoDotTheme: 'purple',
    },
  ]

  return (
    <div className='relative flex h-full flex-col justify-between overflow-auto bg-cool-50 sm:h-auto sm:w-[475px] sm:rounded-lg'>
      <Icon
        name='close'
        size={24}
        className='absolute right-6 top-6 cursor-pointer text-cool-600 duration-200 ease-in-out hover:scale-125'
        onClick={() => setIsOpen(false)}
      />
      <div>
        <header className='px-8 py-6'>
          <h5>{isSell ? t('how_selling_works') : t('how_buying_works')}</h5>
          <p className='mt-1 text-sm text-cool-600'>
            {t('see_more_information')}{' '}
            <Link
              href='/terms-and-conditions'
              target='_blank'
              rel='norefferer'
              className='text-secondary-default'
            >
              {t('terms_&_conditions')}
            </Link>
          </p>
        </header>
        <Separator />

        {/* Restricted Country Message */}
        {/* {isSell && (
          <div className='mx-8 mt-6 flex items-start justify-center space-x-2 bg-warning-default bg-opacity-10 px-4 py-3'>
            <Icon
              name='filled-alert-info'
              filled
              size={16}
              className='mt-[4px] rotate-180 text-warning-default'
            />
            <p className='text-sm'>
              {t('selling_restricted')}{' '}
              <span className='text-base'>
                <ReactCountryFlag countryCode='GB' svg />
              </span>{' '}
              {t('expanding_soon')}
            </p>
          </div>
        )} */}

        <Stepper className='px-8 py-6' steps={isSell ? sellSteps : buySteps} />

        {isSell && (
          <div className='mx-8 mb-8 flex justify-center gap-4 rounded border border-solid border-cool-400 p-4'>
            <Icon
              name='filled-alert-info'
              filled
              size={24}
              className='text-secondary-default'
            />
            <div>
              <p className='mb-1 font-medium text-cool-900'>
                {t('condition_policy')}
              </p>
              <p className='text-sm text-cool-600'>
                {t('marketplace_integrity')}
              </p>
            </div>
          </div>
        )}
      </div>

      <footer className='sticky bottom-0 w-full bg-cool-50 shadow-[0_2px_20px_0_rgba(38,35,56,0.15)] md:shadow-none'>
        {isSell && <Separator className='w-full bg-cool-400' />}

        <div className='px-8 py-6'>
          <div className='flex items-center justify-center space-x-3'>
            <Input
              id={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              data-e2e={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              type='checkbox'
              onChange={e => showAgainHandler(e)}
            />
            <label
              htmlFor={isSell ? 'dont-show-again-sell' : 'dont-show-again-buy'}
              className='cursor-pointer'
            >
              {t('dont_show_again')}
            </label>
          </div>
          <Button
            onClick={() => {
              setIsLoading(true)
            }}
            href={href}
            type='link'
            size='lg'
            className='mt-4 w-full'
            data-e2e='understand-button'
          >
            {isLoading ? <Spinner theme='white' /> : t('common:i_understand')}
          </Button>
        </div>
      </footer>
    </div>
  )
}

type Props = {
  highestBid?: TradePriceInfo | null
  lowestAsk?: TradePriceInfo | null
  // highestBid?: number
  // highestBidCurrency?: Currency
  // lowestAsk?: number
  // lowestAskCurrency?: Currency
  currentEdition?: Edition | null
  artists: Artist[]
  artwork: {
    id: number
    name: string
  }
} & ContainerElement

export const ArtworkTrade = ({
  currentEdition,
  highestBid,
  // highestBidCurrency,
  lowestAsk,
  // lowestAskCurrency,
  artists,
  artwork,
  children,
}: Props) => {
  const { t } = useTranslation('common')
  const setWaysTo = useWaysTo()
  const { setConfig, setIsOpen } = useOverlay()
  const [isLoading, setIsLoading] = useState<IsLoadingInterface>({
    bidOrBuy: false,
    bid: false,
    askOrSell: false,
    ask: false,
  })
  const [isPinned, setIsPinned] = useState(false)
  const { userCurrency } = useCurrencies()

  const buttonsContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onScroll = throttle(() => {
      if (!buttonsContainerRef.current) return

      const { top } = buttonsContainerRef.current.getBoundingClientRect()

      setIsPinned(top <= 64 && top >= 0)
    }, 150)

    window.addEventListener('scroll', onScroll)

    return () => {
      setIsOpen(false)
      window.removeEventListener('scroll', onScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setIsLoadingHandler = useCallback(
    (type: IsLoadingType) =>
      setIsLoading(prevState => ({ ...prevState, [type]: true })),
    []
  )

  const showAgainHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const {
        currentTarget: { id, checked },
      } = e

      if (id === 'dont-show-again-buy') {
        if (checked) {
          localStorage.setItem('hideBuy', String(checked))
          return
        }

        localStorage.removeItem('hideBuy')
        return
      }

      if (id === 'dont-show-again-sell') {
        if (checked) {
          localStorage.setItem('hideSell', String(checked))
          return
        }

        localStorage.removeItem('hideSell')
        return
      }
    },
    []
  )

  const clickHandler = (
    e: MouseEvent<HTMLAnchorElement>,
    type: keyof IsLoadingInterface,
    href: string
  ) => {
    const dontShow = localStorage.getItem(
      type.includes('ask') ? 'hideSell' : 'hideBuy'
    )

    if (!dontShow) {
      e.preventDefault()
      setConfig({
        className: 'lg:flex sm:items-center',
        body: (
          <HowItWorks
            showAgainHandler={showAgainHandler}
            href={href}
            isSell={type.includes('ask')}
          />
        ),
        scrollOnClose: true,
      })
      setIsOpen(true)
      return
    }

    setIsLoadingHandler(type)
  }

  const getNextStepPath = useCallback(
    (root: string) =>
      getArtworkUrlFromRoot(root, artists, artwork.name, artwork.id) +
      `/${currentEdition?.name}`,
    [artists, artwork.id, artwork.name, currentEdition?.name]
  )

  const existingTradePayload = {
    currentEdition,
    artists,
    name: artwork.name,
    id: artwork.id,
  }

  const { editUrl: editAskUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'ask',
  })
  const { editUrl: editBidUrl } = useExistingTrade({
    ...existingTradePayload,
    tradeType: 'bid',
  })

  const bidOrBuyPath = getNextStepPath('bid-or-buy')
  const buyPath = bidOrBuyPath + '?action=buy'
  const bidPath = editBidUrl || bidOrBuyPath + '?action=bid'

  const askOrSellPath = getNextStepPath('ask-or-sell')
  const sellPath = askOrSellPath + '?action=sell'
  const askPath = editAskUrl || askOrSellPath + '?action=ask'

  const hasValues =
    typeof lowestAsk !== 'undefined' && typeof highestBid !== 'undefined'

  return (
    <div className='contents rounded border-cool-300 bg-cool-50 lg:block lg:border lg:px-4 lg:py-5'>
      {children}

      {!hasValues && <Skeleton width='100%' height={48} borderRadius={8} />}

      {hasValues && (
        <div
          ref={buttonsContainerRef}
          className={classNames(
            'sticky top-16 z-10 -mx-6 w-[calc(100%+3rem)] bg-cool-50 p-6 sm:-mx-12 sm:w-[calc(100%+6rem)] sm:px-12 lg:static lg:mx-0 lg:w-full lg:bg-transparent lg:p-0 lg:shadow-none',
            highestBid || lowestAsk
              ? 'flex flex-col space-y-3 lg:space-y-5'
              : 'grid grid-cols-2 gap-x-4',
            isPinned && 'shadow'
          )}
        >
          {lowestAsk ? (
            <div className='grid grid-cols-2 gap-x-4'>
              <TradeButton
                data-e2e='buy-button'
                theme='green'
                href={buyPath}
                value={pickTradePrice(userCurrency, lowestAsk)}
                userCurrency={userCurrency}
                isLoading={isLoading.bidOrBuy}
                onClick={e => clickHandler(e, 'bidOrBuy', buyPath)}
              >
                {t('buy_now')}
              </TradeButton>
              <TradeButton
                data-e2e='bid-button'
                theme='outlineGreen'
                userCurrency={userCurrency}
                href={bidPath}
                isLoading={isLoading.bid}
                onClick={e => clickHandler(e, 'bid', bidPath)}
              >
                {t('make_offer')}
              </TradeButton>
            </div>
          ) : (
            <TradeButton
              data-e2e='bid-button'
              theme='green'
              href={bidPath}
              userCurrency={userCurrency}
              isLoading={isLoading.bid}
              onClick={e => clickHandler(e, 'bid', bidPath)}
            >
              {t('make_offer')}
            </TradeButton>
          )}

          {(highestBid || lowestAsk) && <Separator />}

          {highestBid ? (
            <div className='grid grid-cols-2 gap-x-4'>
              <TradeButton
                data-e2e='sell-button'
                theme='red'
                userCurrency={userCurrency}
                href={sellPath}
                value={pickTradePrice(userCurrency, highestBid)}
                isLoading={isLoading.askOrSell}
                onClick={e => clickHandler(e, 'askOrSell', sellPath)}
              >
                {t('sell_now')}
              </TradeButton>
              <TradeButton
                data-e2e='ask-button'
                theme='outlineRed'
                href={askPath}
                userCurrency={userCurrency}
                isLoading={isLoading.ask}
                onClick={e => clickHandler(e, 'ask', askPath)}
              >
                {t('list_for_more')}
              </TradeButton>
            </div>
          ) : (
            <TradeButton
              data-e2e='ask-button'
              theme='red'
              href={askPath}
              userCurrency={userCurrency}
              isLoading={isLoading.ask}
              onClick={e => clickHandler(e, 'ask', askPath)}
            >
              {t('list_for_sale')}
            </TradeButton>
          )}
        </div>
      )}

      <Info className='lg:mt-5'>
        <Trans
          i18nKey='common:learn_how_buying_or_selling_works'
          components={{
            comp1: (
              <button
                className='text-secondary-default underline'
                onClick={() => setWaysTo('buy')}
              />
            ),
            comp2: (
              <button
                className='text-secondary-default underline'
                onClick={() => setWaysTo('sell')}
              />
            ),
          }}
        />
      </Info>
    </div>
  )
}
